/* 
  New image loader using Webpack's require.context (CRA supports this).
  File pattern expected inside: src/components/MonotonityGame/
    signal1_base.png
    signal1_caret_top.png
    signal1_caret_bottom.png
    signal1_caret_left.png
    signal1_caret_right.png
    signal1_outerline_top.png
    ... etc for 1..6
*/

import chooseRandomly from '../utils/chooseRandomly'
import { PitchKind } from '../utils/pitchKind'

declare const require: {
  context(
    directory: string,
    useSubdirectories: boolean,
    regExp: RegExp
  ): { keys(): string[]; (id: string): string }
}

// ---- Types ----
type SignalId = 1 | 2 | 3 | 4 | 5 | 6

type Catalog = {
  [id in SignalId]: {
    base: string
    variations: string[]
  }
}

// ---- Load all images from MonotonityGame folder ----
// NOTE: the path here is relative to THIS file.
const ctx = require.context(
  '../components/MonotonityGame',
  false,
  /^\.\/signal[1-6]_.+\.(png|jpg|jpeg|webp|svg)$/
)

const IMAGES_RAW: Record<string, string> = {}
ctx.keys().forEach((k) => {
  const clean = k.replace('./', '')
  IMAGES_RAW[clean] = ctx(k)
})

// Build a catalog: id -> { base, variations[] }
function buildCatalog(): Catalog {
  const out = {
    1: { base: '', variations: [] },
    2: { base: '', variations: [] },
    3: { base: '', variations: [] },
    4: { base: '', variations: [] },
    5: { base: '', variations: [] },
    6: { base: '', variations: [] }
  } as Catalog

  const rx = /^signal([1-6])_([^\.]+)\.(png|jpg|jpeg|webp|svg)$/

  Object.keys(IMAGES_RAW).forEach((filename) => {
    const m = filename.match(rx)
    if (!m) return
    const id = Number(m[1]) as SignalId
    const variant = m[2] // "base" or "caret_top" etc
    const url = IMAGES_RAW[filename]

    if (variant === 'base') {
      out[id].base = url
    } else {
      out[id].variations.push(url)
    }
  })

  return out
}

const CATALOG = buildCatalog()

// We keep a module-level cache of variations corresponding
// to the 4 bases chosen at game start, so the slices don't change.
let incorrectSignalImagesCache: string[] = []

// By convention in your current logic:
//   HIGH  -> show a CORRECT (base) image
//   LOW   -> show an INCORRECT (variation) image
const signals = [PitchKind.LOW, PitchKind.HIGH]

export function nextSignal(): PitchKind {
  return chooseRandomly(signals)
}

/**
 * Pick 4 distinct IDs from 1..6 and cache their variations for "incorrect".
 * Returns the 4 base image URLs (used to render at the top bar).
 */
export function generateInitialSignalImages(): string[] {
  const ids: SignalId[] = [1, 2, 3, 4, 5, 6]
  // shuffle then take 4
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
  }
  const chosen = ids.slice(0, 4)

  // cache all variations belonging to the chosen bases
  incorrectSignalImagesCache = chosen.flatMap((id) => CATALOG[id].variations)

  // return the 4 base URLs in a randomized order (as before)
  return chosen.map((id) => CATALOG[id].base)
}

/**
 * Choose the next image to show in a round:
 * - If signal === HIGH  -> pick a BASE from initialSignalImages (correct),
 * - If signal === LOW   -> pick a VARIATION from the cached variations (incorrect).
 * Both exclude the previous image to avoid immediate repeats.
 */
export function nextSignalImage(
  signal: PitchKind,
  initialSignalImages: string[],
  prevSignalImage: string | null
): string {
  let pool: string[] = []

  if (signal === PitchKind.HIGH) {
    pool = initialSignalImages
  } else {
    pool = incorrectSignalImagesCache
  }

  const options = prevSignalImage ? pool.filter((x) => x !== prevSignalImage) : pool
  if (options.length === 0) {
    // fallback: if filtered everything, ignore the prev exclusion
    return chooseRandomly(pool)
  }
  return chooseRandomly(options)
}
